import React, { useState, useEffect } from 'react';
import { engineRoleAPI } from '../../../../services';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import './EngineRoleManager.css';

function EngineRoleManager() {
  const [engines, setEngines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [selectedEngine, setSelectedEngine] = useState(null);
  const [stepDownReason, setStepDownReason] = useState('');
  const [showStepDownDialog, setShowStepDownDialog] = useState(false);
  const [showSetRoleDialog, setShowSetRoleDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [stepDownStats, setStepDownStats] = useState(null);
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch service status to check if this instance can manage roles
      const serviceRes = await engineRoleAPI.getServiceStatus();
      setServiceStatus(serviceRes.data);

      // Fetch engine status with details
      const statusRes = await engineRoleAPI.getStatusDetailed();
      setEngines(statusRes.data.engines || []);
    } catch (error) {
      console.error('Error fetching engine data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await engineRoleAPI.requestServiceInfo();
      // Wait a moment for engines to report
      setTimeout(async () => {
        const statusRes = await engineRoleAPI.getStatusDetailed();
        setEngines(statusRes.data.engines || []);
        setRefreshing(false);
      }, 2000);
    } catch (error) {
      console.error('Error refreshing data:', error);
      setRefreshing(false);
    }
  };

  const handleSelectPrimary = async () => {
    try {
      const response = await engineRoleAPI.selectPrimary();
      if (response.data.success) {
        alert(`Successfully selected ${response.data.selectedPrimary} as primary engine`);
        fetchAllData();
      }
    } catch (error) {
      console.error('Error selecting primary:', error);
      alert('Failed to select primary engine');
    }
  };

  const handleSetRole = async () => {
    if (!selectedEngine || !selectedRole) return;
    
    try {
      const response = await engineRoleAPI.setRole(selectedEngine.endpoint, selectedRole);
      if (response.data.success) {
        alert(`Successfully set ${selectedEngine.endpoint} role to ${selectedRole}`);
        setShowSetRoleDialog(false);
        setSelectedEngine(null);
        setSelectedRole('');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error setting role:', error);
      alert('Failed to set engine role');
    }
  };

  const handleStepDown = async () => {
    if (!selectedEngine) return;
    
    try {
      const response = await engineRoleAPI.stepDown(selectedEngine.endpoint, stepDownReason);
      if (response.data.success) {
        alert(`Successfully requested step down for ${selectedEngine.endpoint}`);
        setShowStepDownDialog(false);
        setSelectedEngine(null);
        setStepDownReason('');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error stepping down engine:', error);
      alert('Failed to step down engine');
    }
  };

  const handleShowStats = async () => {
    try {
      const response = await engineRoleAPI.getStepDownStats();
      setStepDownStats(response.data.statistics);
      setShowStatsDialog(true);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const formatUptime = (milliseconds) => {
    if (!milliseconds) return 'N/A';
    
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="engine-role-container">
        <div className="content-header">
          <h2>Engine Role Management</h2>
          <p>Manage trade engine roles and monitor their status</p>
        </div>
        <LoadingSpinner message="Loading engine data..." />
      </div>
    );
  }

  const canManageRoles = serviceStatus?.serviceStatus?.isPrimary;

  return (
    <div className="engine-role-container">
      <div className="content-header">
        <h2>Engine Role Management</h2>
        <p>Manage trade engine roles and monitor their status</p>
      </div>

      {/* Service Status Alert */}
      <div className={`service-status-alert ${canManageRoles ? 'primary' : 'secondary'}`}>
        <i className={`fas ${canManageRoles ? 'fa-crown' : 'fa-shield-alt'}`}></i>
        <span>
          This service instance is <strong>{canManageRoles ? 'PRIMARY' : 'SECONDARY'}</strong>
          {canManageRoles ? ' and can manage engine roles' : ' (read-only access)'}
        </span>
      </div>

      {/* Actions Bar */}
      <div className="actions-bar">
        <button 
          className="btn btn-primary"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <i className={`fas fa-sync ${refreshing ? 'fa-spin' : ''}`}></i>
          {refreshing ? 'Refreshing...' : 'Refresh Status'}
        </button>
        
        {canManageRoles && (
          <>
            <button 
              className="btn btn-success"
              onClick={handleSelectPrimary}
            >
              <i className="fas fa-magic"></i> Auto-Select Primary
            </button>
            
            <button 
              className="btn btn-info"
              onClick={handleShowStats}
            >
              <i className="fas fa-chart-bar"></i> Step Down Stats
            </button>
          </>
        )}
      </div>

      {/* Engines Table */}
      <div className="engines-table-container">
        <table className="engines-table">
          <thead>
            <tr>
              <th>Endpoint</th>
              <th>Status</th>
              <th>Role</th>
              <th>Version</th>
              <th>Uptime</th>
              <th>Last Update</th>
              {canManageRoles && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {engines.map((engine) => (
              <tr key={engine.endpoint} className={`engine-row ${engine.role}`}>
                <td>
                  <div className="endpoint-info">
                    <i className="fas fa-server"></i>
                    <span>{engine.endpoint}</span>
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${engine.connected ? 'connected' : 'disconnected'}`}>
                    <i className={`fas ${engine.connected ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                    {engine.connected ? 'Connected' : 'Disconnected'}
                  </span>
                </td>
                <td>
                  <span className={`role-badge ${engine.role}`}>
                    {engine.role === 'primary' && <i className="fas fa-crown"></i>}
                    {engine.role === 'secondary' && <i className="fas fa-shield-alt"></i>}
                    {engine.role === 'standby' && <i className="fas fa-pause-circle"></i>}
                    {engine.role?.toUpperCase() || 'N/A'}
                  </span>
                </td>
                <td>{engine.serviceInfo?.version || 'N/A'}</td>
                <td>{formatUptime(engine.serviceInfo?.uptime)}</td>
                <td>{formatDate(engine.lastUpdate)}</td>
                {canManageRoles && (
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon"
                        onClick={() => {
                          setSelectedEngine(engine);
                          setShowSetRoleDialog(true);
                        }}
                        title="Set Role"
                        disabled={!engine.connected}
                      >
                        <i className="fas fa-user-tag"></i>
                      </button>
                      {engine.role === 'primary' && (
                        <button
                          className="btn-icon danger"
                          onClick={() => {
                            setSelectedEngine(engine);
                            setShowStepDownDialog(true);
                          }}
                          title="Step Down"
                        >
                          <i className="fas fa-level-down-alt"></i>
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Set Role Dialog */}
      {showSetRoleDialog && (
        <div className="dialog-overlay">
          <div className="dialog">
            <h3>Set Engine Role</h3>
            <p>Set role for {selectedEngine?.endpoint}</p>
            <select 
              value={selectedRole} 
              onChange={(e) => setSelectedRole(e.target.value)}
              className="role-select"
            >
              <option value="">Select Role</option>
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="standby">Standby</option>
            </select>
            <div className="dialog-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowSetRoleDialog(false);
                  setSelectedEngine(null);
                  setSelectedRole('');
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSetRole}
                disabled={!selectedRole}
              >
                Set Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step Down Dialog */}
      {showStepDownDialog && (
        <div className="dialog-overlay">
          <div className="dialog">
            <h3>Step Down Engine</h3>
            <p>Step down {selectedEngine?.endpoint} from primary role</p>
            <input
              type="text"
              placeholder="Reason for step down"
              value={stepDownReason}
              onChange={(e) => setStepDownReason(e.target.value)}
              className="reason-input"
            />
            <div className="dialog-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowStepDownDialog(false);
                  setSelectedEngine(null);
                  setStepDownReason('');
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleStepDown}
                disabled={!stepDownReason}
              >
                Step Down
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Dialog */}
      {showStatsDialog && stepDownStats && (
        <div className="dialog-overlay">
          <div className="dialog stats-dialog">
            <h3>Step Down Statistics</h3>
            <div className="stats-content">
              <div className="stat-item">
                <label>Total Step Downs:</label>
                <span>{stepDownStats.totalStepDowns}</span>
              </div>
              <div className="stat-item">
                <label>Average Duration:</label>
                <span>{formatUptime(stepDownStats.averageDuration)}</span>
              </div>
              <div className="stat-item">
                <label>Most Common Reason:</label>
                <span>{stepDownStats.mostCommonReason || 'N/A'}</span>
              </div>
              {stepDownStats.engineStats && (
                <div className="engine-stats">
                  <h4>Per Engine Stats</h4>
                  {Object.entries(stepDownStats.engineStats).map(([endpoint, stats]) => (
                    <div key={endpoint} className="engine-stat">
                      <strong>{endpoint}:</strong>
                      <span>{stats.stepDownCount} step downs, Total: {formatUptime(stats.totalDuration)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="dialog-actions">
              <button 
                className="btn btn-primary"
                onClick={() => setShowStatsDialog(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EngineRoleManager; 